import { useState, useEffect, useRef } from 'react';
import { useTerminal } from '@/layouts/LocalStoreLayout';
import { localPosAPI, localCashRegisterAPI } from '@/services/localApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, Trash2, ShoppingCart, CreditCard,
  Banknote, Smartphone, Pause, Receipt, AlertCircle
} from 'lucide-react';

const LocalPOS = () => {
  const { terminalId, storeInfo, isOnline } = useTerminal();
  const searchRef = useRef(null);

  const [register, setRegister] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [cart, setCart] = useState([]);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [heldBills, setHeldBills] = useState([]);

  useEffect(() => {
    loadRegisterStatus();
    loadHeldBills();
    searchRef.current?.focus();
  }, [terminalId]);

  const loadRegisterStatus = async () => {
    try {
      setRegisterLoading(true);
      const res = await localCashRegisterAPI.getCurrent(terminalId);
      setRegister(res.data.register);
    } catch (error) {
      console.error('Failed to load register:', error);
    } finally {
      setRegisterLoading(false);
    }
  };

  const loadHeldBills = async () => {
    try {
      const res = await localPosAPI.getHeldBills(terminalId);
      setHeldBills(res.data.held_bills || []);
    } catch (error) {
      console.error('Failed to load held bills:', error);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const res = await localPosAPI.searchProducts(query, 10);
      setSearchResults(res.data.products || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    const hasUomConversion = (product.uom_conversion_factor || 1) > 1;
    
    if (existing) {
      const increment = existing.allow_decimal_qty ? 0.1 : 1;
      const newQty = Math.round((existing.quantity + increment) * 100) / 100;
      const taxAmount = existing.selling_price * newQty * (existing.tax_rate || 0) / 100;
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: newQty, tax_amount: taxAmount, total: existing.selling_price * newQty + taxAmount }
          : item
      ));
    } else {
      const taxAmount = product.selling_price * (product.tax_rate || 0) / 100;
      setCart([...cart, {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        quantity: 1,
        mrp: product.mrp || product.selling_price,
        selling_price: product.selling_price,
        tax_rate: product.tax_rate || 0,
        tax_amount: taxAmount,
        discount: 0,
        total: product.selling_price + taxAmount,
        // UOM conversion info
        base_uom: product.base_uom || 'PCS',
        purchase_uom: product.purchase_uom || null,
        uom_conversion_factor: product.uom_conversion_factor || 1,
        allow_decimal_qty: hasUomConversion
      }]);
    }

    setSearchQuery('');
    setSearchResults([]);
    searchRef.current?.focus();
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        // For products with UOM conversion, use smaller increments
        const increment = item.allow_decimal_qty ? 0.1 : 1;
        const actualDelta = delta * (item.allow_decimal_qty ? increment : 1);
        
        let newQty = item.quantity + actualDelta;
        const minQty = item.allow_decimal_qty ? 0.1 : 1;
        newQty = Math.max(minQty, Math.round(newQty * 100) / 100);
        
        const taxAmount = item.selling_price * newQty * item.tax_rate / 100;
        return {
          ...item,
          quantity: newQty,
          tax_amount: taxAmount,
          total: item.selling_price * newQty + taxAmount
        };
      }
      return item;
    }));
  };

  const updateQuantityDirect = (productId, newQty) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        // Allow empty or partial decimal input while typing
        if (newQty === '' || newQty === '.') {
          return { ...item, quantity: newQty };
        }
        
        let qty = parseFloat(newQty);
        
        // If not valid number, keep raw string for typing
        if (isNaN(qty)) {
          return { ...item, quantity: newQty };
        }
        
        // Valid number - calculate totals
        if (qty <= 0) qty = 0.1;
        qty = Math.round(qty * 100) / 100;
        
        const taxAmount = item.selling_price * qty * item.tax_rate / 100;
        return {
          ...item,
          quantity: qty,
          tax_amount: taxAmount,
          total: item.selling_price * qty + taxAmount
        };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName('');
    setCustomerMobile('');
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.selling_price * item.quantity), 0);
    const taxAmount = cart.reduce((sum, item) => sum + item.tax_amount, 0);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (!register) {
      toast.error('Please open cash register first');
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    const { subtotal, taxAmount, total } = calculateTotals();

    try {
      setProcessing(true);
      
      const saleData = {
        terminal_id: terminalId,
        register_id: register.id,
        customer_name: customerName || null,
        customer_mobile: customerMobile || null,
        items: cart,
        subtotal,
        tax_amount: taxAmount,
        discount_amount: 0,
        total_amount: total,
        payment_mode: paymentMode,
        created_by: 'pos_user'
      };

      const res = await localPosAPI.createSale(saleData);
      
      setLastSale({
        bill_number: res.data.bill_number,
        total: res.data.total_amount
      });
      
      toast.success(`Bill ${res.data.bill_number} created!`);
      clearCart();
      setShowPaymentModal(false);
      loadRegisterStatus();

    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleHoldBill = async () => {
    if (cart.length === 0) return;

    try {
      const { subtotal } = calculateTotals();
      await localPosAPI.holdBill({
        terminal_id: terminalId,
        customer_name: customerName,
        items: cart,
        subtotal,
        held_by: 'pos_user'
      });

      toast.success('Bill held successfully');
      clearCart();
      loadHeldBills();
    } catch (error) {
      toast.error('Failed to hold bill');
    }
  };

  const handleRecallBill = async (holdId) => {
    try {
      const res = await localPosAPI.getHeldBill(holdId);
      setCart(res.data.items || []);
      setCustomerName(res.data.customer_name || '');
      await localPosAPI.deleteHeldBill(holdId);
      loadHeldBills();
      toast.success('Bill recalled');
    } catch (error) {
      toast.error('Failed to recall bill');
    }
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  if (registerLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Loading...</p>
      </div>
    );
  }

  // No register open - show warning
  if (!register) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center">
          <AlertCircle className="w-10 h-10 text-yellow-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">No Open Register</h2>
        <p className="text-gray-500 text-center max-w-md">
          You need to open a cash register before you can process sales.
          Go to Cash Register to open one.
        </p>
        <Button onClick={() => window.location.href = '/local-store/register'}>
          Open Cash Register
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-100px)]" data-testid="local-pos-page">
      {/* Left - Product Search & Results */}
      <div className="w-1/2 border-r flex flex-col bg-white">
        {/* Search Bar */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Scan barcode or search product..."
              className="pl-10 text-lg h-12"
              data-testid="product-search"
            />
          </div>
        </div>

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {searching && <p className="text-gray-500">Searching...</p>}
          
          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((product) => (
                <div
                  key={product.id}
                  onClick={() => addToCart(product)}
                  className="bg-gray-50 hover:bg-blue-50 border rounded-lg p-3 cursor-pointer transition-colors"
                  data-testid={`product-result-${product.sku}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">₹{product.selling_price}</p>
                      {product.tax_rate > 0 && (
                        <p className="text-xs text-gray-500">+{product.tax_rate}% GST</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Held Bills */}
          {heldBills.length > 0 && searchQuery === '' && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-500 mb-2">HELD BILLS</h3>
              <div className="space-y-2">
                {heldBills.map((bill) => (
                  <div
                    key={bill.id}
                    onClick={() => handleRecallBill(bill.id)}
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 cursor-pointer hover:bg-yellow-100"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{bill.customer_name || 'Guest'}</p>
                        <p className="text-sm text-gray-500">{bill.items?.length || 0} items</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">₹{bill.subtotal?.toFixed(2)}</p>
                        <Badge variant="secondary">Held</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right - Cart */}
      <div className="w-1/2 flex flex-col bg-gray-50">
        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <ShoppingCart className="w-16 h-16 mb-4" />
              <p>Cart is empty</p>
              <p className="text-sm">Search products to add</p>
            </div>
          ) : (
            <div className="space-y-2">
              {cart.map((item, index) => (
                <div key={item.product_id} className="bg-white rounded-lg p-3 border">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-500">₹{item.selling_price} × {item.quantity}</p>
                    </div>
                    <p className="font-bold text-lg">₹{item.total.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(item.product_id, -1)}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*\.?[0-9]*"
                        value={item.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || value === '.' || /^\d*\.?\d*$/.test(value)) {
                            updateQuantityDirect(item.product_id, value);
                          }
                        }}
                        onBlur={(e) => {
                          const qty = parseFloat(e.target.value);
                          if (isNaN(qty) || qty <= 0) {
                            updateQuantityDirect(item.product_id, item.allow_decimal_qty ? '0.1' : '1');
                          }
                        }}
                        className="w-20 text-center font-medium border rounded px-2 py-1 text-sm"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(item.product_id, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => removeFromCart(item.product_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* UOM Conversion Info */}
                  {item.allow_decimal_qty && (
                    <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      1 {item.purchase_uom} = {item.uom_conversion_factor} {item.base_uom}
                      <span className="ml-2 text-gray-500">
                        (Qty {item.quantity} = {(item.quantity * item.uom_conversion_factor).toFixed(1)} {item.base_uom})
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Summary & Actions */}
        <div className="bg-white border-t p-4 space-y-4">
          {/* Totals */}
          <div className="space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Tax</span>
              <span>₹{taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span>₹{total.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              onClick={clearCart}
              disabled={cart.length === 0}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              onClick={handleHoldBill}
              disabled={cart.length === 0}
            >
              <Pause className="w-4 h-4 mr-1" />
              Hold
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCheckout}
              disabled={cart.length === 0}
              data-testid="checkout-btn"
            >
              Pay ₹{total.toFixed(2)}
            </Button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Payment</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Customer Name</label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500">Mobile</label>
                <Input
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="text-sm text-gray-500 block mb-2">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={paymentMode === 'cash' ? 'default' : 'outline'}
                  onClick={() => setPaymentMode('cash')}
                  className="h-20 flex-col gap-2"
                >
                  <Banknote className="w-6 h-6" />
                  Cash
                </Button>
                <Button
                  variant={paymentMode === 'card' ? 'default' : 'outline'}
                  onClick={() => setPaymentMode('card')}
                  className="h-20 flex-col gap-2"
                >
                  <CreditCard className="w-6 h-6" />
                  Card
                </Button>
                <Button
                  variant={paymentMode === 'upi' ? 'default' : 'outline'}
                  onClick={() => setPaymentMode('upi')}
                  className="h-20 flex-col gap-2"
                >
                  <Smartphone className="w-6 h-6" />
                  UPI
                </Button>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gray-100 rounded-lg p-4 text-center">
              <p className="text-gray-500">Amount to Pay</p>
              <p className="text-4xl font-bold text-green-600">₹{total.toFixed(2)}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handlePayment}
              disabled={processing}
              data-testid="confirm-payment-btn"
            >
              {processing ? 'Processing...' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Last Sale Toast */}
      {lastSale && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white rounded-lg p-4 shadow-lg animate-in slide-in-from-right">
          <div className="flex items-center gap-3">
            <Receipt className="w-6 h-6" />
            <div>
              <p className="font-bold">{lastSale.bill_number}</p>
              <p>₹{lastSale.total.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalPOS;
